/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { EStationStatus } from '../../api/station/types';
import { unavailableStationAndConnectorStatuses } from '../../constants';

import connectorStatusStyles from './connector-status.styles';

type Props = {
    stationStatus: EStationStatus;
    connectorStatus: EStationStatus;
    isUnavailable?: boolean;
};

const availableStatuses = [EStationStatus.AVAILABLE];
const busyStatuses = [
    EStationStatus.CHARGING,
    EStationStatus.PREPARING,
    EStationStatus.SUSPENDED_EV,
    EStationStatus.SUSPENDED_EVSE,
    EStationStatus.FINISHING,
];

const ConnectorStatus = (props: Props) => {
    const { stationStatus, connectorStatus, isUnavailable = false } = props;
    const intl = useIntl();

    const css = useMemo(() => {
        if (
            unavailableStationAndConnectorStatuses.includes(stationStatus) ||
            unavailableStationAndConnectorStatuses.includes(connectorStatus) ||
            isUnavailable
        ) {
            return [connectorStatusStyles.commonStyles, connectorStatusStyles.unavailable];
        } else if (
            availableStatuses.includes(stationStatus) &&
            availableStatuses.includes(connectorStatus)
        ) {
            return [connectorStatusStyles.commonStyles, connectorStatusStyles.available];
        } else if (busyStatuses.includes(connectorStatus)) {
            return [connectorStatusStyles.commonStyles, connectorStatusStyles.busy];
        }
    }, [stationStatus, connectorStatus, isUnavailable]);

    const status = useMemo(() => {
        if (
            unavailableStationAndConnectorStatuses.includes(stationStatus) ||
            unavailableStationAndConnectorStatuses.includes(connectorStatus) ||
            isUnavailable
        ) {
            return intl.formatMessage({ id: 'connector.statuses.unavailable' });
        } else {
            return intl.formatMessage({
                id: `connector.statuses.${connectorStatus.toLowerCase()}`,
            });
        }
    }, [stationStatus, connectorStatus, intl, isUnavailable]);

    return <span css={css}>{status}</span>;
};

export default ConnectorStatus;
